import { useState, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import PropTypes from 'prop-types'
import { IoChevronDown } from 'react-icons/io5'
import { MdOutlineVerified } from 'react-icons/md'
import Input from '../../../components/common/Input/index'
import Select from '../../../components/common/Select/index'
import Button from '../../../components/common/Button/index'
import Typography from '../../../components/common/Typography/index'
import {
  sendDirectNotification,
  sendGdprPrivacyPolicyNotification,
} from '../../../state/redux/actions/notificationActions'

const NotificationForm = ({ onToggle, error }) => {
  const dispatch = useDispatch()
  const { sendLoading, error: notificationError } = useSelector(state => state.notifications)

  const [formData, setFormData] = useState({
    title: '',
    type: 'PROPERTY_UPDATE', // Default to General notification
    description: '',
    hyperlink: '',
    version: '',
    buttonTitle: '',
    file: null,
    addToGdpr: true,
    // Updated recipient options to match API expected values
    recipients: {
      agents: false,
      buyers: false,
      admin: false,
    },
  })

  const [formError, setFormError] = useState('')
  const [successModalOpen, setSuccessModalOpen] = useState(false)
  const [localLoading, setLocalLoading] = useState(false)
  const isSubmittingRef = useRef(false)

  const handleRecipientChange = recipientType => {
    setFormData(prev => ({
      ...prev,
      recipients: {
        ...prev.recipients,
        [recipientType]: !prev.recipients[recipientType],
      },
    }))
  }

  const handleChange = e => {
    const { name, value, type, files } = e.target

    // Clear previous errors when user starts typing
    if (formError) setFormError('')

    if (type === 'file') {
      setFormData(prev => ({
        ...prev,
        [name]: files[0],
      }))
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value,
      }))
    }
  }

  const validateForm = () => {
    // Check if at least one recipient is selected
    const hasRecipient = Object.values(formData.recipients).some(recipient => recipient)

    if (!formData.title.trim()) {
      setFormError('Title is required')
      return false
    }

    if (!formData.description.trim()) {
      setFormError('Description is required')
      return false
    }

    if (!formData.type) {
      setFormError('Notification type is required')
      return false
    }

    // Only require recipients for regular notifications, not GDPR notifications
    if (!formData.addToGdpr && !hasRecipient) {
      setFormError('At least one recipient must be selected')
      return false
    }

    return true
  }

  const prepareNotificationPayload = () => {
    // Get selected recipients with corrected mapping
    const selectedRecipients = Object.keys(formData.recipients)
      .filter(key => formData.recipients[key])
      .map(key => {
        // Map internal names to API expected values
        const recipientMap = {
          agents: 'agents',
          buyers: 'buyers',
          realtors: 'realtors',
          coRealtors: 'co-realtors', // Note the hyphen for co-realtors
          admin: 'admin',
        }
        return recipientMap[key] || key
      })

    // Use FormData for file upload support
    const formDataPayload = new FormData()
    formDataPayload.append('title', formData.title.trim())
    formDataPayload.append('description', formData.description.trim())
    formDataPayload.append('type', formData.type)

    // Append recipients as individual array items instead of JSON string
    selectedRecipients.forEach((recipient, index) => {
      formDataPayload.append(`recipients[${index}]`, recipient)
    })

    // Add optional fields if they have values
    if (formData.hyperlink && formData.hyperlink.trim()) {
      formDataPayload.append('hyperlink', formData.hyperlink.trim())
    }

    if (formData.version && formData.version.trim()) {
      formDataPayload.append('version', formData.version.trim())
    }

    if (formData.buttonTitle && formData.buttonTitle.trim()) {
      formDataPayload.append('buttonTitle', formData.buttonTitle.trim())
    }

    // Add GDPR flag
    formDataPayload.append('addToGdpr', formData.addToGdpr)

    // Add file if uploaded
    if (formData.file) {
      formDataPayload.append('attachment', formData.file)
    }

    return formDataPayload
  }

  const handleSubmit = async e => {
    e.preventDefault()
    e.stopPropagation()

    // Prevent multiple rapid submissions using ref
    if (isSubmittingRef.current || sendLoading || localLoading) {
      console.log('Submission blocked - already in progress')
      return
    }

    // Set the ref to prevent concurrent submissions
    isSubmittingRef.current = true

    // Clear previous errors
    setFormError('')
    setLocalLoading(true)

    // Validate form
    if (!validateForm()) {
      setLocalLoading(false)
      isSubmittingRef.current = false
      return
    }

    try {
      let result

      // Check if this is a GDPR notification
      if (formData.addToGdpr) {
        // Prepare GDPR-specific payload
        const gdprPayload = {
          title: formData.title.trim(),
          eventName: formData.title.trim(), // Backend requires this field
          uploadUrl: formData.hyperlink?.trim() || '', // Backend requires this field
          message: formData.description.trim(),
          version: formData.version?.trim() || '1.0',
          data: {
            policyUrl: formData.hyperlink?.trim() || '',
            actionText: formData.buttonTitle?.trim() || 'Review Policy',
            additionalProp1: {},
          },
          priority: formData.type === 'SYSTEM_ALERT' ? 'high' : 'medium',
        }

        console.log('Sending GDPR privacy policy notification:', gdprPayload)
        result = await dispatch(sendGdprPrivacyPolicyNotification(gdprPayload)).unwrap()
      } else {
        // Use regular notification endpoint with FormData
        const payload = prepareNotificationPayload()

        console.log('Sending regular notification with FormData payload')
        console.log('Title:', formData.title)
        console.log('Type:', formData.type)
        console.log(
          'Recipients:',
          Object.keys(formData.recipients).filter(key => formData.recipients[key])
        )
        console.log('Has attachment:', formData.file ? 'Yes' : 'No')

        result = await dispatch(sendDirectNotification(payload)).unwrap()
      }

      // Show success modal
      setSuccessModalOpen(true)

      // Reset form after successful submission
      setFormData({
        title: '',
        type: 'PROPERTY_UPDATE', // Reset to default type
        description: '',
        hyperlink: '',
        version: '',
        buttonTitle: '',
        file: null,
        addToGdpr: true,
        recipients: {
          agents: false,
          buyers: false,
          admin: false,
        },
      })

      // Clear file input
      const fileInput = document.getElementById('file')
      if (fileInput) {
        fileInput.value = ''
      }

      console.log('Notification sent successfully:', result)
    } catch (error) {
      console.error('Error sending notification:', error)
      setFormError(error || 'Failed to send notification. Please try again.')
    } finally {
      setLocalLoading(false)
      // Reset the ref to allow future submissions
      isSubmittingRef.current = false
    }
  }

  return (
    <div className="max-w-md w-full mx-auto max-h-[90vh] flex flex-col">
      <div className="bg-primary py-4 px-8 rounded-t-lg flex-shrink-0">
        <Typography variant="h3" className="text-white">
          Notification
        </Typography>
      </div>
      <form onSubmit={handleSubmit} className="px-8 py-6 bg-white rounded-b-lg overflow-y-auto scrollbar-hide">
        <div className="flex flex-col gap-4">
          {/* Notification Type Dropdown */}
          <div className="w-full flex flex-col items-center justify-center leading-none">
            <div className="w-full max-w-md">
              <div className="w-full">
                <Select
                  id="type"
                  name="type"
                  value={formData.type}
                  onChange={handleChange}
                  options={[
                    { value: 'PROPERTY_UPDATE', label: 'General notification/ Discover notification' },
                    { value: 'SYSTEM_ALERT', label: 'System Alert' },
                    { value: 'PROMOTIONAL', label: 'Promotional' },
                  ]}
                  required
                  className="w-full leading-none"
                  icon={<IoChevronDown className="text-black" />}
                />
              </div>

              {/* Recipients */}
              <div className="w-full mt-4 text-center">
                <Typography className="mb-2 text-sm font-medium">Recipients:</Typography>
                <div className="flex flex-row gap-6 justify-center">
                  {/* BUYER APP */}
                  <div className="flex flex-row items-center gap-2 whitespace-nowrap">
                    <Input
                      id="buyers"
                      name="buyers"
                      type="checkbox"
                      checked={formData.recipients.buyers}
                      onChange={() => handleRecipientChange('buyers')}
                      className="w-4 h-4"
                    />
                    <label htmlFor="buyers" className="text-sm cursor-pointer">
                      Buyer app
                    </label>
                  </div>

                  {/* MAVEN APP */}
                  <div className="flex flex-row items-center gap-2 whitespace-nowrap">
                    <Input
                      id="agents"
                      name="agents"
                      type="checkbox"
                      checked={formData.recipients.agents}
                      onChange={() => handleRecipientChange('agents')}
                      className="w-4 h-4"
                    />
                    <label htmlFor="agents" className="text-sm cursor-pointer">
                      Maven app
                    </label>
                  </div>

                  {/* ADMIN PANEL */}
                  <div className="flex flex-row items-center gap-2 whitespace-nowrap">
                    <Input
                      id="admin"
                      name="admin"
                      type="checkbox"
                      checked={formData.recipients.admin}
                      onChange={() => handleRecipientChange('admin')}
                      className="w-4 h-4"
                    />
                    <label htmlFor="admin" className="text-sm cursor-pointer">
                      Admin panel
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Notification Title */}
          <div className="w-full">
            <label htmlFor="title" className="block text-sm font-medium mb-1">
              Notification title
            </label>
            <Input
              id="title"
              name="title"
              value={formData.title}
              onChange={handleChange}
              placeholder="Event name"
              required
              className="w-full h-8 py-1 text-sm leading-tight"
            />
          </div>

          {/* Description */}
          <div className="w-full">
            <label htmlFor="description" className="block text-sm font-medium mb-1">
              Description
            </label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              placeholder="What will we do"
              required
              rows="3"
              className="w-full text-xs px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
            />
          </div>

          {/* Hyperlink */}
          <div className="w-full">
            <label htmlFor="hyperlink" className="block text-sm font-medium mb-1">
              Hyperlink (if Applicable)
            </label>
            <Input
              id="hyperlink"
              name="hyperlink"
              value={formData.hyperlink}
              onChange={handleChange}
              placeholder="Enter URL"
              className="w-full h-8 py-1 text-sm leading-tight"
            />
          </div>

          {/* Version Note */}
          <div className="w-full">
            <Typography className="text-xs font-semibold text-gray-600 mb-2">
              If this is a T's & C's or Privacy policy update notification, note the new version here (if applicable)
            </Typography>
            <Input
              id="version"
              name="version"
              value={formData.version}
              onChange={handleChange}
              placeholder="v1.8363.32"
              className="w-full h-8 py-1 text-sm leading-tight"
            />
          </div>

          {/* Button Title */}
          <div className="w-full">
            <label htmlFor="buttonTitle" className="block text-sm font-medium mb-1">
              Button title
            </label>
            <Input
              id="buttonTitle"
              name="buttonTitle"
              value={formData.buttonTitle}
              onChange={handleChange}
              placeholder="I agree"
              className="w-full h-8 py-1 text-sm leading-tight"
            />
          </div>

          {/* --- IMAGE UPLOAD SECTION (UPDATED) --- */}
          <div className="w-full mt-2 flex flex-col items-center">
            <div className="flex flex-col items-start gap-2">
              <div className="text-sm font-medium text-gray-900">Image upload</div>
              <div className="relative">
                <label
                  htmlFor="file"
                  className="cursor-pointer flex gap-3 px-10 py-2 border bg-gray-100 border-gray-300 rounded-lg hover:bg-gray-50 transition-colors min-w-[180px]"
                >
                  <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"
                    />
                  </svg>
                  <span className="text-sm font-medium text-gray-500">Upload File</span>
                </label>
                <Input
                  id="file"
                  name="file"
                  type="file"
                  onChange={handleChange}
                  className="hidden"
                  accept=".jpg,.jpeg,.png,.pdf,.doc,.docx"
                />
              </div>
            </div>
            {formData.file && (
              <Typography className="text-xs text-gray-600 mt-2">Selected: {formData.file.name}</Typography>
            )}
          </div>

          {/* --- GDPR CHECKBOX (UPDATED) --- */}
          <div className="w-full flex justify-center items-center mt-4 mb-2">
            <div className="flex items-center gap-2">
              <div className="relative inline-flex items-center">
                <input
                  id="addToGdpr"
                  name="addToGdpr"
                  type="checkbox"
                  checked={formData.addToGdpr}
                  onChange={e => setFormData(prev => ({ ...prev, addToGdpr: e.target.checked }))}
                  className="peer w-5 h-5 appearance-none border-2 border-gray-300 rounded checked:bg-[#36cf4e] checked:border-[#36cf4e] cursor-pointer transition-all"
                />
                {formData.addToGdpr && (
                  <svg
                    className="absolute w-3 h-3 left-1 top-1 pointer-events-none"
                    fill="none"
                    stroke="white"
                    strokeWidth="3"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </div>
              <label htmlFor="addToGdpr" className="text-sm text-gray-800 cursor-pointer">
                Add to user GDPR status info
              </label>
            </div>
          </div>

          {/* Error Messages */}
          {(error || formError || notificationError) && (
            <div className="text-danger text-sm text-center p-2 bg-red-100 rounded" role="alert">
              {error || formError || notificationError}
            </div>
          )}

          {/* Share Button */}
          <Button
            variant="primary"
            type="submit"
            className="w-full bg-[#0fa798] hover:bg-[#0d8c7f] text-white py-2 rounded-md text-sm mt-2"
            disabled={sendLoading || localLoading}
          >
            {sendLoading || localLoading ? 'Sending...' : 'Share'}
          </Button>
        </div>
      </form>

      {/* Success Modal */}
      {successModalOpen && (
        <div className="fixed inset-0 bg-black/30 backdrop-blur-sm z-50 flex items-center justify-center">
          <div className="bg-white rounded-lg p-8 max-w-sm w-full mx-4 shadow-xl">
            <div className="flex flex-col items-center justify-center gap-4">
              <div className="flex items-center gap-2">
                <MdOutlineVerified className="text-[#36cf4e] text-4xl" />
                <Typography variant="h3" className="text-gray-900 font-semibold">
                  Notification Sent Successfully!
                </Typography>
              </div>
              <Typography className="text-sm text-gray-600 text-center">
                Your notification has been sent to all selected recipients.
              </Typography>
              <Button
                variant="primary"
                className="bg-[#0fa798] hover:bg-[#0d8c7f] text-white px-8 py-2 rounded-md mt-2"
                onClick={() => {
                  setSuccessModalOpen(false)
                  onToggle()
                }}
              >
                Done
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

NotificationForm.propTypes = {
  checked: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
  error: PropTypes.string,
}

NotificationForm.defaultProps = {
  error: null,
  onToggle: () => {
    console.warn('onToggle function not provided')
  },
}

export default NotificationForm
